import { useQuery } from '@tanstack/react-query';
import apiClient from './apiClient';
import { config } from '../config';
import { useAppContext } from '../libs/appContextLib';

export const USER_PREFERENCES_QUERY_KEY_ROOT = 'user-preferences';

export const getUserPreferencesQueryKey = (userId, activeAccountId) => [
  USER_PREFERENCES_QUERY_KEY_ROOT,
  userId,
  activeAccountId,
];

export const invalidateUserPreferencesQuery = (queryClient, userId, activeAccountId) => {
  if (!queryClient) {
    return Promise.resolve();
  }

  const queryKey = userId && activeAccountId
    ? getUserPreferencesQueryKey(userId, activeAccountId)
    : userId
      ? [USER_PREFERENCES_QUERY_KEY_ROOT, userId]
      : [USER_PREFERENCES_QUERY_KEY_ROOT];

  return queryClient.invalidateQueries({ queryKey });
};

/**
 * Hook to fetch user-specific navigation preferences.
 *
 * Fetches from GET /configure/navigation/user-preferences
 *
 * Returns:
 * - pinnedRoutes: Array of route IDs the user has pinned
 * - recentRoutes: Array of recent navigation history entries
 * - navigationPreferences: Object with UI preferences (collapsed categories, etc.)
 *
 * This is separate from company-level route settings (routePreferences)
 * which come from /configure/data/status.
 */
export const useFetchUserPreferences = () => {
  const { currUser } = useAppContext();
  const preferencesUserKey = currUser?.id || currUser?.userId;
  const activeAccountId = currUser?.customerId;

  return useQuery({
    queryKey: getUserPreferencesQueryKey(preferencesUserKey, activeAccountId),
    queryFn: async () => {
      const { data } = await apiClient.get(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`
      );
      return data;
    },
    // Scope by both identity and active account because the endpoint merges company defaults.
    enabled: !!preferencesUserKey && !!activeAccountId,
    staleTime: 5 * 60 * 1000, // 5 minutes - user preferences don't change frequently
  });
};

export default useFetchUserPreferences;
